/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title as ChartTitle,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from 'chart.js';
import {
    Visibility,
    AttachMoney,
    Send as SendIcon,
    ChatBubbleOutlineOutlined,
} from '@mui/icons-material';
import { AgingData, AgingDrillDownItem, VelocityPoint } from 'hooks/analytics';
import { SectionCard, SectionTitle, Grid, AnalyticsColors } from '../../components/analyticsUi';
import InfoTooltip from 'components/v2/common/infoTooltip';
import AgingBucketChart from '../../components/AgingBucketChart';
import DrillDownPanel from '../../components/DrillDownPanel';
import ClickableName from '../../components/ClickableName';
import ActionMenu from '../../components/ActionMenu';
import { AnalyticsActions } from '../../hooks/useAnalyticsActions';
import { formatCurrency } from 'utils/formatters';
import { REVENUE_RECOVERY, REVENUE_PAYMENT_VELOCITY } from '../../constants/metricDescriptions';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ChartTitle,
    ChartTooltip,
    Legend,
    Filler
);

interface RevenueRecoveryProps {
    unconvertedOffers: AgingData;
    unpaidContracts: AgingData;
    paymentVelocity: VelocityPoint[];
    actions: AnalyticsActions;
    onBucketDrillDown: (type: string, bucketIndex: number) => Promise<AgingDrillDownItem[]>;
}

const RevenueRecovery = ({
    unconvertedOffers,
    unpaidContracts,
    paymentVelocity,
    actions,
    onBucketDrillDown,
}: RevenueRecoveryProps) => {
    const [drillDown, setDrillDown] = useState<{
        title: string;
        items: AgingDrillDownItem[];
    } | null>(null);

    const currentVelocity = paymentVelocity[paymentVelocity.length - 1]?.avgDaysToPayment || 0;
    const previousVelocity =
        paymentVelocity.length >= 2
            ? paymentVelocity[paymentVelocity.length - 2]?.avgDaysToPayment
            : null;
    const velocityDiff = previousVelocity != null ? previousVelocity - currentVelocity : null;
    const improving = velocityDiff != null && velocityDiff > 0;

    const handleOfferBucketClick = async (bucketIndex: number) => {
        const bucket = unconvertedOffers.buckets[bucketIndex];
        const items = await onBucketDrillDown('offers', bucketIndex);
        setDrillDown({ title: `Unconverted Offers \u2014 ${bucket.label}`, items });
    };

    const handleContractBucketClick = async (bucketIndex: number) => {
        const bucket = unpaidContracts.buckets[bucketIndex];
        const items = await onBucketDrillDown('contracts', bucketIndex);
        setDrillDown({ title: `Unpaid Contracts \u2014 ${bucket.label}`, items });
    };

    const lineData = useMemo(
        () => ({
            labels: paymentVelocity.map((p) => p.period),
            datasets: [
                {
                    label: 'Avg Days to Payment',
                    data: paymentVelocity.map((p) => p.avgDaysToPayment),
                    borderColor: '#2563EB',
                    backgroundColor: (ctx: any) => {
                        const chart = ctx.chart;
                        const { ctx: canvasCtx, chartArea } = chart;
                        if (!chartArea) return 'rgba(37, 99, 235, 0.08)';
                        const gradient = canvasCtx.createLinearGradient(
                            0,
                            chartArea.top,
                            0,
                            chartArea.bottom
                        );
                        gradient.addColorStop(0, 'rgba(37, 99, 235, 0.15)');
                        gradient.addColorStop(1, 'rgba(37, 99, 235, 0.02)');
                        return gradient;
                    },
                    fill: true,
                    tension: 0.35,
                    pointRadius: 5,
                    pointBackgroundColor: '#FFFFFF',
                    pointBorderColor: '#2563EB',
                    pointBorderWidth: 2.5,
                    pointHoverRadius: 7,
                    borderWidth: 2.5,
                },
            ],
        }),
        [paymentVelocity]
    );

    const lineOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: '#1F2937',
                    titleFont: { size: 12, weight: '600' as const },
                    bodyFont: { size: 13 },
                    padding: 10,
                    cornerRadius: 8,
                    callbacks: {
                        label: (ctx: any) => ` ${ctx.raw.toFixed(1)} days avg`,
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: false,
                    ticks: {
                        callback: (val: any) => `${val} days`,
                        font: { size: 11 },
                        color: '#9CA3AF',
                    },
                    grid: { color: '#F3F4F6' },
                    border: { display: false },
                },
                x: {
                    grid: { display: false },
                    ticks: {
                        font: { size: 11 },
                        color: '#6B7280',
                    },
                    border: { display: false },
                },
            },
        }),
        []
    );

    return (
        <>
            <SectionCard>
                <SectionTitle>
                    Revenue Recovery
                    <InfoTooltip title={REVENUE_RECOVERY} />
                </SectionTitle>

                <Grid columns={2} gap="2rem">
                    <div>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: AnalyticsColors.textPrimary,
                                m: '0 0 1rem 0',
                                textTransform: 'uppercase',
                                letterSpacing: '0.4px',
                            }}
                        >
                            Unconverted Offers
                        </Typography>
                        <AgingBucketChart
                            buckets={unconvertedOffers.buckets}
                            total={unconvertedOffers.total}
                            totalCount={unconvertedOffers.totalCount}
                            onBucketClick={handleOfferBucketClick}
                        />
                    </div>
                    <div>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: AnalyticsColors.textPrimary,
                                m: '0 0 1rem 0',
                                textTransform: 'uppercase',
                                letterSpacing: '0.4px',
                            }}
                        >
                            Unpaid Contracts
                        </Typography>
                        <AgingBucketChart
                            buckets={unpaidContracts.buckets}
                            total={unpaidContracts.total}
                            totalCount={unpaidContracts.totalCount}
                            onBucketClick={handleContractBucketClick}
                        />
                    </div>
                </Grid>
            </SectionCard>

            <SectionCard>
                <SectionTitle>
                    Payment Velocity
                    <InfoTooltip title={REVENUE_PAYMENT_VELOCITY} />
                </SectionTitle>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem', mb: '0.5rem' }}>
                    <Box
                        component="span"
                        sx={{
                            fontSize: 32,
                            fontWeight: 700,
                            color: AnalyticsColors.textPrimary,
                            lineHeight: 1,
                        }}
                    >
                        {currentVelocity.toFixed(1)}
                    </Box>
                    <div>
                        <Box
                            component="span"
                            sx={{ fontSize: 13, color: AnalyticsColors.textSecondary }}
                        >
                            avg days to payment (current month)
                        </Box>
                        {velocityDiff != null && (
                            <div style={{ marginTop: '4px' }}>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        p: '3px 10px',
                                        borderRadius: '6px',
                                        background: improving ? '#DCFCE7' : '#FEE2E2',
                                        color: improving ? '#166534' : '#991B1B',
                                    }}
                                >
                                    {improving ? '↓' : '↑'} {Math.abs(velocityDiff).toFixed(1)}d vs
                                    last month
                                </Box>
                            </div>
                        )}
                    </div>
                </Box>

                <Box sx={{ height: 240, mt: '1rem' }}>
                    <Line data={lineData} options={lineOptions as any} />
                </Box>
            </SectionCard>

            {drillDown && (
                <DrillDownPanel<AgingDrillDownItem>
                    open
                    onClose={() => setDrillDown(null)}
                    title={drillDown.title}
                    subtitle={`${drillDown.items.length} clients · ${formatCurrency(drillDown.items.reduce((s, i) => s + i.amount, 0))} total`}
                    items={drillDown.items}
                    columns={[
                        {
                            label: 'Client',
                            render: (item) => (
                                <ClickableName
                                    onClick={() => actions.navigateToClient(item.clientId)}
                                >
                                    {item.candidateName}
                                </ClickableName>
                            ),
                        },
                        { label: 'Program', render: (item) => item.programName },
                        {
                            label: 'Amount',
                            align: 'right',
                            render: (item) => (
                                <span
                                    style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {formatCurrency(item.amount)}
                                </span>
                            ),
                        },
                        {
                            label: 'Days',
                            align: 'right',
                            render: (item) => (
                                <span
                                    style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {item.daysSince}d
                                </span>
                            ),
                        },
                    ]}
                    renderActions={(item) => (
                        <ActionMenu
                            isOwner
                            items={[
                                {
                                    label: 'View Contract',
                                    icon: <Visibility sx={{ fontSize: 14 }} />,
                                    onClick: () =>
                                        actions.navigateToContract(
                                            item.clientId,
                                            item.applicationId
                                        ),
                                },
                                {
                                    label: 'Mark Paid',
                                    icon: <AttachMoney sx={{ fontSize: 14 }} />,
                                    onClick: () =>
                                        actions.markPaid(
                                            item.candidateName,
                                            item.applicationId,
                                            item.milestoneId
                                        ),
                                },
                                {
                                    label: 'Resend Invoice',
                                    icon: <SendIcon sx={{ fontSize: 14 }} />,
                                    onClick: () =>
                                        actions.resendInvoice(
                                            item.candidateName,
                                            item.applicationId,
                                            item.milestoneId
                                        ),
                                },
                                {
                                    label: 'Message Client',
                                    icon: <ChatBubbleOutlineOutlined sx={{ fontSize: 14 }} />,
                                    onClick: () =>
                                        actions.openMessageComposer(
                                            item.chatGroupId,
                                            item.candidateName,
                                            'outstanding payment'
                                        ),
                                },
                            ]}
                        />
                    )}
                />
            )}
        </>
    );
};

export default RevenueRecovery;
